import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Save } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { RubyLoader } from '../../../components/RubyLoader';
import {
  putAiAction, putAiVibe, rubyAiErrorText,
  type AiActionDto, type AiCatalogResponse, type AiVibeDto,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import RoutingTab from '../tabs/RoutingTab';
import CoverageTab from '../coverage/CoverageTab';
import RoutingMap from './RoutingMap';
import {
  ACTION_GROUP_ORDER, ACTION_STATUS_LABEL, GROUP_LABELS, UNMAPPED_AI_CALLS, WIRED_ACTION_IDS,
  actionStatus, actionUnit, effectiveMultiplier, estimateCharge, estimateStepsChargeUsd, findModel,
  marginFromMultiplier, modelDisplayName, normalizeUsedFor, pct, seasonLabel, typicalRunCost,
  vendorPriceLabel, vibeUnlockedOn, AI_FEATURE_LABELS, type ActionStatus, type FamilyTier,
} from './catalogModel';
import {
  actionDraftDirty, actionModelOptions, buildActionDraft, buildActionPatch, stepModelOptions,
  validateActionDraft, type ActionDraft,
} from './actionDraft';
import { AI_MODEL_USED_FOR, formatRubyTokens, tokensLabel, usd } from './units';
import { RubyToken } from '../../../components/RubyToken';
import { useAiFamily, useRubyAiCatalog, useRubyAiUsage } from './useRubyAi';
import { Badge, Btn, Card, CardHeader, ErrorBanner, FieldLabel, Issues, Modal, Mono, TextInput, Toggle } from './ui';

const STATUS_TONE: Record<ActionStatus, 'green' | 'stone' | 'amber'> = {
  live: 'green', off: 'stone', not_wired: 'stone', to_map: 'amber',
};

const noop = () => {};

/**
 * Picker labels for a known step id (spec mockup board 2: "Draws with" / "If
 * it fails" / "Background model"). An unrecognised step id (a future action)
 * falls back to its own id so the picker never renders blank.
 */
const STEP_PICKER_LABELS: Record<string, { primary: string; fallback?: string }> = {
  draw: { primary: 'Draws with', fallback: 'If it fails' },
  background: { primary: 'Background model' },
  // 2026-10-15 — data.area_insights
  records: { primary: 'Tax & sale records' },
  home_values: { primary: 'Home values & forecast' },
  people: { primary: 'People & income' },
  schools: { primary: 'Schools' },
  safety: { primary: 'Crime rates' },
  photo: { primary: 'Photo & map' },
};

/**
 * Ruby AI Actions (mockup board 2) — the upgraded Routing tab (D1).
 *
 * Each ACTION# row: default + fallback model (limited to models whose "used
 * for" matches the action's group), a base multiplier, and SEPARATE minimum
 * and maximum charges in Ruby Tokens (D3). Below: the vibes on the action
 * with the tier that unlocks each, an estimated tenant price and the margin
 * (1 − 1/multiplier), and the AI calls in code that are not an action yet.
 * The legacy ROLE# routing stays available — it is still the fallback for
 * callers not converted to an action.
 */
const ActionsTab: React.FC<AiTabProps> = (props) => {
  const { onHeaderStats, showToast } = props;
  const [params, setParams] = useSearchParams();
  const { catalog, error, reload, setCatalog } = useRubyAiCatalog();
  const { family } = useAiFamily();
  const { usage } = useRubyAiUsage();

  const actions = catalog?.actions ?? [];
  const selectedId = params.get('action') && actions.some((a) => a.actionId === params.get('action'))
    ? params.get('action')!
    : (actions.find((a) => WIRED_ACTION_IDS.has(a.actionId))?.actionId ?? actions[0]?.actionId);
  const selected = actions.find((a) => a.actionId === selectedId) ?? null;

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('action', id);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!catalog) return;
    const live = catalog.actions.filter((a) => actionStatus(a, catalog.models, AI_MODEL_USED_FOR) === 'live').length;
    onHeaderStats([
      { label: 'Actions live', value: `${live} / ${catalog.actions.length}`, tone: 'emerald' },
      { label: 'Vibes', value: String(catalog.vibes.length), tone: 'white' },
      { label: 'Unmapped AI calls', value: String(UNMAPPED_AI_CALLS.length), tone: 'rose' },
    ]);
  }, [catalog, onHeaderStats]);

  if (error && !catalog) return <ErrorBanner title="Failed to load Ruby AI Actions" detail={error} />;
  if (!catalog) return <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />;

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-5">
        <aside className="lg:w-[300px] flex-shrink-0 space-y-3" aria-label="Actions">
          {ACTION_GROUP_ORDER.map((g) => {
            const rows = actions.filter((a) => a.group === g).sort((a, b) =>
              Number(WIRED_ACTION_IDS.has(b.actionId)) - Number(WIRED_ACTION_IDS.has(a.actionId)) || a.label.localeCompare(b.label));
            if (!rows.length) return null;
            return (
              <div key={g} className="space-y-2">
                <p className="text-[12px] uppercase tracking-wider text-stone-600 font-semibold">{GROUP_LABELS[g]}</p>
                {rows.map((a) => {
                  const st = actionStatus(a, catalog.models, AI_MODEL_USED_FOR);
                  const nVibes = catalog.vibes.filter((v) => v.actionIds.includes(a.actionId)).length;
                  const active = a.actionId === selectedId;
                  return (
                    <button key={a.actionId} type="button" onClick={() => select(a.actionId)} aria-current={active}
                      className={`w-full text-left bg-white rounded-xl px-3.5 py-3 min-h-[44px] flex items-center justify-between gap-2 ${active ? 'border-2 border-[#DC143C]' : 'border border-stone-200 hover:border-stone-300'}`}>
                      <span className="min-w-0">
                        <span className="block font-semibold text-sm text-stone-900 truncate">{a.label}</span>
                        <span className="block text-[12px] text-stone-600">
                          {actionUnit(a, catalog.models)}{nVibes ? ` · ${nVibes} vibe${nVibes === 1 ? '' : 's'}` : ''}
                        </span>
                      </span>
                      <Badge tone={STATUS_TONE[st]}>{ACTION_STATUS_LABEL[st]}</Badge>
                    </button>
                  );
                })}
              </div>
            );
          })}
          <p className="text-[12px] text-stone-600 px-0.5">Actions are seeded in code. Types can be added to each group later.</p>
        </aside>

        <section className="flex-1 min-w-0 space-y-4">
          {selected ? (
            <>
              <ActionEditor
                key={`${selected.actionId}-${selected.updatedAt ?? ''}`}
                action={selected}
                catalog={catalog}
                usage={usage}
                onSaved={(row) => {
                  setCatalog((c) => c && ({ ...c, actions: c.actions.map((a) => (a.actionId === row.actionId ? { ...a, ...row } : a)) }));
                  void reload();
                }}
                showToast={showToast}
              />
              <VibesOnAction
                action={selected}
                catalog={catalog}
                tiers={family?.tiers ?? []}
                usage={usage}
                onChanged={reload}
                showToast={showToast}
              />
            </>
          ) : (
            <Card className="p-8 text-center text-sm text-stone-600">No actions are seeded yet. Run the Ruby AI seed script for this stage.</Card>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5 text-[13px] text-amber-900 space-y-1.5">
            <p className="font-semibold">AI calls that aren’t an action yet (found in code)</p>
            <p className="font-mono text-[12px] leading-relaxed">
              {UNMAPPED_AI_CALLS.map((c, i) => (
                <span key={`${c.service}-${c.call}`}>
                  {i > 0 && '  ·  '}{c.service} · {c.call}{c.note ? ` (${c.note})` : ''}
                  {c.candidateFor ? ` → ${actions.find((a) => a.actionId === c.candidateFor)?.label ?? c.candidateFor}?` : ''}
                </span>
              ))}
            </p>
            {usage && (
              <p className="text-[12px]">
                Unmapped AI spend, last 30 days: <strong>{usd(usage.vendorSpend.unmappedCostUsd)}</strong> in vendor cost —
                charged by legacy per-call pricing until each caller becomes an action.
              </p>
            )}
          </div>
        </section>
      </div>

      <RoutingMap actions={catalog.actions} vibes={catalog.vibes} models={catalog.models} />

      <Collapsible title="Legacy role routing" hint="ROLE# rows — still the fallback for every caller not yet converted to a Ruby AI Action.">
        <RoutingTab {...props} onHeaderStats={noop} />
      </Collapsible>
      <Collapsible title="AI coverage" hint="Which modules and entities the AI agents can see and act on.">
        <CoverageTab {...props} onHeaderStats={noop} />
      </Collapsible>
    </>
  );
};

const Collapsible: React.FC<{ title: string; hint: string; children: React.ReactNode }> = ({ title, hint, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full min-h-[44px] px-4 md:px-5 py-3 flex items-center gap-3 text-left">
        {open ? <ChevronDown size={16} className="text-stone-500" /> : <ChevronRight size={16} className="text-stone-500" />}
        <span>
          <span className="block text-sm font-semibold text-stone-900">{title}</span>
          <span className="block text-[12px] text-stone-600">{hint}</span>
        </span>
      </button>
      {open && <div className="px-4 md:px-5 pb-5 space-y-5">{children}</div>}
    </Card>
  );
};

// ---------------------------------------------------------------------------

const ActionEditor: React.FC<{
  action: AiActionDto;
  catalog: AiCatalogResponse;
  usage: ReturnType<typeof useRubyAiUsage>['usage'];
  onSaved: (row: AiActionDto) => void;
  showToast: AiTabProps['showToast'];
}> = ({ action, catalog, usage, onSaved, showToast }) => {
  const original = useMemo(() => buildActionDraft(action), [action]);
  const [draft, setDraft] = useState<ActionDraft>(original);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const set = (p: Partial<ActionDraft>) => setDraft((d) => ({ ...d, ...p }));

  const issues = useMemo(() => validateActionDraft(draft, action, catalog.models, AI_MODEL_USED_FOR), [draft, action, catalog.models]);
  const dirty = actionDraftDirty(original, draft);

  const providerLabel = useCallback((id: string) => catalog.providers.find((p) => p.providerId === id)?.label ?? id, [catalog.providers]);
  const options = (current: string) =>
    actionModelOptions(catalog.models, action, AI_MODEL_USED_FOR, current, providerLabel, (m) => vendorPriceLabel(m.unitPrices));
  const stepOptions = (capability: string, current: string) =>
    stepModelOptions(catalog.models, capability, AI_MODEL_USED_FOR, current, providerLabel, (m) => vendorPriceLabel(m.unitPrices));
  const setStep = (id: string, patch: { primary?: string; fallback?: string }) =>
    setDraft((d) => ({ ...d, steps: d.steps.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));

  const primaryModel = findModel(catalog.models, ...(draft.primary.split('#') as [string, string]));
  const usesSearch = primaryModel ? normalizeUsedFor(primaryModel.capabilities, AI_MODEL_USED_FOR).usedFor.includes('web_search') : false;
  const wired = WIRED_ACTION_IDS.has(action.actionId);

  const typical = typicalRunCost(usage, action.actionId, null);
  const mult = Number(draft.multiplier);
  const est = typical && Number.isFinite(mult)
    ? estimateCharge({
      vendorCostUsd: typical.costUsd, multiplier: mult, minTokens: Number(draft.minTokens) || 0, maxTokens: Number(draft.maxTokens) || 0,
      creditUnitPriceUsd: catalog.config.creditUnitPriceUsd,
    })
    : null;

  // Multi-step pipeline (e.g. image.single_object: draw → background) — the
  // estimate comes from vendor typical-usage pricing per step, not real spend,
  // since a step chain has no single "typical run" row in usage yet.
  const stepsCostUsd = action.steps?.length ? estimateStepsChargeUsd(action.steps, catalog.models) : 0;
  const stepsEst = action.steps?.length && Number.isFinite(mult)
    ? estimateCharge({
      vendorCostUsd: stepsCostUsd, multiplier: mult, minTokens: Number(draft.minTokens) || 0, maxTokens: Number(draft.maxTokens) || 0,
      creditUnitPriceUsd: catalog.config.creditUnitPriceUsd,
    })
    : null;

  const save = async (patchOverride?: Record<string, unknown>) => {
    const patch = patchOverride ?? buildActionPatch(original, draft);
    if (!Object.keys(patch).length) return;
    setSaving(true);
    setServerError(null);
    try {
      const r = await putAiAction(action.actionId, patch);
      showToast(`${action.label} saved`, 'success');
      onSaved(r.row ?? { ...action, ...(patch as Partial<AiActionDto>) });
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to save the action'));
    } finally {
      setSaving(false);
    }
  };

  const groupWord = GROUP_LABELS[action.group];

  return (
    <Card className="p-4 md:p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-stone-900">{action.label}</h2>
          <p className="text-[13px] text-stone-600">
            action id <Mono>{action.actionId}</Mono> · package feature <Mono>{action.featureId}</Mono>
            {AI_FEATURE_LABELS[action.featureId] ? ` (${AI_FEATURE_LABELS[action.featureId]})` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn onClick={() => set({ enabled: !draft.enabled })} disabled={saving}>
            {draft.enabled ? 'Disable' : 'Enable'}
          </Btn>
          <Btn kind="primary" onClick={() => void save()} busy={saving} disabled={!dirty || issues.errors.length > 0}>
            <Save size={16} /> Save changes
          </Btn>
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-xl px-3.5 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[13px]">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-stone-600">Where it’s triggered</p>
          <p className="font-semibold text-stone-900">{action.trigger?.surface ?? '—'}</p>
          {action.trigger?.file && <Mono className="text-stone-600">{action.trigger.file}</Mono>}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-stone-600">Where it runs</p>
          <p className="font-semibold text-stone-900">{action.runs?.service ?? (wired ? 'pos-agents-service' : 'Not wired yet')}</p>
          {action.runs?.endpoint && <Mono className="text-stone-600">{action.runs.endpoint}</Mono>}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-stone-600">Steps</p>
          <p className="font-semibold text-stone-900">
            {!wired ? 'No generator in code yet' : usesSearch ? 'Web search → write' : 'Write'}
          </p>
          {wired && (
            <Mono className="text-stone-600">
              {draft.fallback ? `fallback: ${modelDisplayName(catalog.models, ...(draft.fallback.split('#') as [string, string]))} writes from inputs` : 'no fallback'}
            </Mono>
          )}
        </div>
      </div>

      {!draft.enabled && (
        <p className="text-[12px] text-stone-700 bg-stone-100 rounded-lg px-3 py-2">
          This action is <strong>off</strong>{dirty && original.enabled ? ' (unsaved)' : ''}. Tenants cannot run it{wired ? '' : ' — and it has no generator in code yet'}.
        </p>
      )}

      {draft.steps.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {draft.steps.map((s) => {
            const labels = STEP_PICKER_LABELS[s.id] ?? { primary: `${s.id} model` };
            return (
              <React.Fragment key={s.id}>
                <div>
                  <FieldLabel>{labels.primary}</FieldLabel>
                  <SearchableDropdown
                    options={stepOptions(s.capability, s.primary)} value={s.primary || null}
                    onChange={(v) => setStep(s.id, { primary: v || '' })}
                    placeholder={`Choose a model`} searchPlaceholder="Search models…" ariaLabel={labels.primary} disabled={saving}
                  />
                </div>
                {labels.fallback && (
                  <div>
                    <FieldLabel>{labels.fallback}</FieldLabel>
                    <SearchableDropdown
                      options={[{ value: '', label: 'No fallback', subtitle: 'The step fails if the default fails' }, ...stepOptions(s.capability, s.fallback).filter((o) => o.value !== s.primary)]}
                      value={s.fallback}
                      onChange={(v) => setStep(s.id, { fallback: v || '' })}
                      placeholder="No fallback" searchPlaceholder="Search models…" ariaLabel={labels.fallback} disabled={saving}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Default model</FieldLabel>
            <SearchableDropdown
              options={options(draft.primary)} value={draft.primary || null}
              onChange={(v) => set({ primary: v || '' })}
              placeholder={`Choose a ${groupWord} model`} searchPlaceholder="Search models…" ariaLabel="Default model" disabled={saving}
            />
          </div>
          <div>
            <FieldLabel>Fallback model</FieldLabel>
            <SearchableDropdown
              options={[{ value: '', label: 'No fallback', subtitle: 'The run fails if the default fails' }, ...options(draft.fallback).filter((o) => o.value !== draft.primary)]}
              value={draft.fallback}
              onChange={(v) => set({ fallback: v || '' })}
              placeholder="No fallback" searchPlaceholder="Search models…" ariaLabel="Fallback model" disabled={saving}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <FieldLabel htmlFor="ac-mult">Base multiplier</FieldLabel>
          <TextInput id="ac-mult" inputMode="decimal" value={draft.multiplier} suffix="×" onChange={(e) => set({ multiplier: e.target.value })} disabled={saving} />
        </div>
        <div>
          <FieldLabel htmlFor="ac-min">Minimum charge per run</FieldLabel>
          <TextInput id="ac-min" inputMode="numeric" value={draft.minTokens} suffix="Ruby Tokens" onChange={(e) => set({ minTokens: e.target.value.replace(/[^\d]/g, '') })} disabled={saving} />
        </div>
        <div>
          <FieldLabel htmlFor="ac-max">Maximum charge per run</FieldLabel>
          <TextInput id="ac-max" inputMode="numeric" value={draft.maxTokens} suffix="Ruby Tokens" onChange={(e) => set({ maxTokens: e.target.value.replace(/[^\d]/g, '') })} disabled={saving} />
        </div>
      </div>

      <p className="text-[12px] text-stone-600">
        Model lists only show models marked “{groupWord}”{action.group === 'text' ? ' (or Web search / Legal)' : ''} on Providers &amp; Models.
        {' '}The maximum is also what the wallet holds before the vendor is called.
        {typical && est && (
          <> A typical run costs us ≈ {usd(typical.costUsd)} (average of {typical.runs} run{typical.runs === 1 ? '' : 's'}, last 30 days); at {draft.multiplier} × a tenant pays ≈ <strong>{tokensLabel(est.rubyTokens)}</strong>
            {est.clamped ? ` (${est.clamped === 'min' ? 'raised to the minimum' : 'capped at the maximum'})` : ''}.</>
        )}
      </p>

      {stepsEst && (
        <p className="text-[12px] text-stone-600">
          From vendor typical-usage pricing per step, at {draft.multiplier} × a tenant pays ≈ <strong>{tokensLabel(stepsEst.rubyTokens)} per picture</strong>
          {stepsEst.clamped ? ` (${stepsEst.clamped === 'min' ? 'raised to the minimum' : 'capped at the maximum'})` : ''}.
        </p>
      )}

      <Issues errors={serverError ? [...issues.errors, serverError] : issues.errors} warnings={dirty ? issues.warnings : []} />
    </Card>
  );
};

// ---------------------------------------------------------------------------

const VibesOnAction: React.FC<{
  action: AiActionDto;
  catalog: AiCatalogResponse;
  tiers: FamilyTier[];
  usage: ReturnType<typeof useRubyAiUsage>['usage'];
  onChanged: () => Promise<void> | void;
  showToast: AiTabProps['showToast'];
}> = ({ action, catalog, tiers, usage, onChanged, showToast }) => {
  const [, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPick, setAssignPick] = useState<string | null>(null);

  const vibes = catalog.vibes
    .filter((v) => v.actionIds.includes(action.actionId))
    .sort((a, b) => (Number(b.defaultFor.includes(action.actionId)) - Number(a.defaultFor.includes(action.actionId)))
      || (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.label.localeCompare(b.label));
  const unassigned = catalog.vibes.filter((v) => !v.actionIds.includes(action.actionId));

  const openVibe = (vibeId: string) => {
    const next = new URLSearchParams();
    next.set('tab', 'vibes');
    next.set('vibe', vibeId);
    setParams(next, { replace: true });
  };

  const toggle = async (v: AiVibeDto, enabled: boolean) => {
    setBusy(v.vibeId);
    try {
      await putAiVibe(v.vibeId, { enabled });
      showToast(`${v.label} ${enabled ? 'on' : 'off'}`, 'success');
      await onChanged();
    } catch (err) {
      showToast(rubyAiErrorText(err, 'Failed to update the vibe'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const assign = async () => {
    const v = catalog.vibes.find((x) => x.vibeId === assignPick);
    if (!v) return;
    setBusy('assign');
    try {
      await putAiVibe(v.vibeId, { actionIds: [...v.actionIds, action.actionId] });
      showToast(`${v.label} assigned to ${action.label}`, 'success');
      setAssignOpen(false);
      setAssignPick(null);
      await onChanged();
    } catch (err) {
      showToast(rubyAiErrorText(err, 'Failed to assign the vibe'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Vibes on this action"
        hint="A vibe’s multiplier replaces the base. The tenant sees the price before generating."
        actions={<>
          <Btn onClick={() => setAssignOpen(true)} disabled={!unassigned.length}>Assign existing</Btn>
          <Btn kind="soft" onClick={() => {
            const next = new URLSearchParams(); next.set('tab', 'vibes'); next.set('vibe', 'new'); next.set('action', action.actionId);
            setParams(next, { replace: true });
          }}><Plus size={16} /> New vibe</Btn>
        </>}
      />
      {vibes.length === 0 ? (
        <p className="p-6 text-sm text-stone-600 text-center">No vibes on this action — it runs with its base prompt and base multiplier.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
                <th className="text-left font-semibold px-4 py-2.5">Vibe</th>
                <th className="text-left font-semibold px-3 py-2.5">Model</th>
                <th className="text-left font-semibold px-3 py-2.5">Multiplier</th>
                <th className="text-left font-semibold px-3 py-2.5">Available</th>
                <th className="text-left font-semibold px-3 py-2.5">Unlocked on</th>
                <th className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">Tenant pays</th>
                <th className="text-left font-semibold px-3 py-2.5">Margin</th>
                <th className="text-left font-semibold px-3 py-2.5">On</th>
              </tr>
            </thead>
            <tbody>
              {vibes.map((v) => {
                const mult = effectiveMultiplier(v, action);
                const typical = typicalRunCost(usage, action.actionId, v.vibeId);
                const est = typical ? estimateCharge({
                  vendorCostUsd: typical.costUsd, multiplier: mult, minTokens: action.minTokens, maxTokens: action.maxTokens,
                  creditUnitPriceUsd: catalog.config.creditUnitPriceUsd,
                }) : null;
                const hasModel = !!(v.providerId && v.modelId);
                const vm = hasModel ? findModel(catalog.models, v.providerId, v.modelId) : undefined;
                const am = findModel(catalog.models, action.providerId, action.modelId);
                const tag = vm && am ? tagCheaper(vm.unitPrices, am.unitPrices) : null;
                return (
                  <tr key={v.vibeId} className="border-t border-stone-100">
                    <td className="px-4 py-2">
                      <button type="button" onClick={() => openVibe(v.vibeId)} className="min-h-[44px] inline-flex items-center gap-2 font-semibold text-stone-900 hover:text-[#9F1239]">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: v.color ?? '#a1a1aa' }} />
                        {v.label}
                        {v.defaultFor.includes(action.actionId) && <Badge tone="black">DEFAULT</Badge>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-stone-700">
                      {hasModel ? modelDisplayName(catalog.models, v.providerId, v.modelId) : 'Action default'}
                      {tag && <span className={`ml-1 text-[11px] ${tag === 'cheaper' ? 'text-emerald-700' : 'text-[#9F1239]'}`}>{tag}</span>}
                    </td>
                    <td className="px-3 py-2 font-semibold text-stone-900 whitespace-nowrap">{mult} ×</td>
                    <td className="px-3 py-2 text-stone-700">{seasonLabel(v.season)}</td>
                    <td className="px-3 py-2 text-stone-700">{vibeUnlockedOn(tiers, action, v)}</td>
                    <td className="px-3 py-2 text-stone-700 whitespace-nowrap" title={typical ? `from ${typical.runs} run(s) ${typical.basis === 'vibe' ? 'with this vibe' : 'of this action'}` : 'no runs yet'}>
                      {est ? <span className="inline-flex items-center gap-1"><RubyToken size={14} />≈ {formatRubyTokens(est.rubyTokens)}</span> : '—'}
                    </td>
                    <td className="px-3 py-2 text-emerald-700 whitespace-nowrap">≈ {pct(marginFromMultiplier(mult))}</td>
                    <td className="px-3 py-1">
                      <Toggle on={v.enabled} busy={busy === v.vibeId} label={`${v.enabled ? 'Turn off' : 'Turn on'} ${v.label}`} onChange={(on) => void toggle(v, on)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={assignOpen} onClose={() => setAssignOpen(false)} busy={busy === 'assign'}
        title={`Assign a vibe to ${action.label}`}
        subtitle="The vibe keeps its own prompt, model, multiplier, season and tier gate."
        footer={<>
          <Btn onClick={() => setAssignOpen(false)}>Cancel</Btn>
          <Btn kind="primary" onClick={() => void assign()} busy={busy === 'assign'} disabled={!assignPick}>Assign</Btn>
        </>}
      >
        <SearchableDropdown
          options={unassigned.map((v) => ({ value: v.vibeId, label: v.label, subtitle: `${effectiveMultiplier(v, action)} × · ${seasonLabel(v.season)}` }))}
          value={assignPick} onChange={setAssignPick} placeholder="Choose a vibe" searchPlaceholder="Search vibes…" ariaLabel="Vibe to assign"
        />
      </Modal>
    </Card>
  );
};

/** Compare two models' output-token price (else any unit price) for the "cheaper"/"premium" hint. */
function tagCheaper(a: Record<string, number | undefined>, b: Record<string, number | undefined>): 'cheaper' | 'premium' | null {
  const price = (p: Record<string, number | undefined>) =>
    p.output_per_million_usd ?? p.per_call_usd ?? p.per_image_usd ?? p.per_second_usd ?? p.input_per_million_usd;
  const pa = price(a); const pb = price(b);
  if (typeof pa !== 'number' || typeof pb !== 'number' || pa === pb) return null;
  // Units must match to compare; a per-call grounding model vs a token model is apples to oranges.
  const unit = (p: Record<string, number | undefined>) =>
    p.output_per_million_usd != null ? 'tok' : p.per_call_usd != null ? 'call' : p.per_image_usd != null ? 'img' : 'other';
  if (unit(a) !== unit(b)) return null;
  return pa < pb ? 'cheaper' : 'premium';
}

export default ActionsTab;
